import { useEffect, useMemo, useState } from "react";
import { BarChart3, RefreshCcw } from "lucide-react";
import api from "../services/api";
import ViewerSidebar from "../components/ViewerSidebar";
import { downloadEsmpFile } from "../utils/fileDownload";

const normalizeLabel = (value, fallback) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed ? trimmed : fallback;
};

const parseEsmpListPayload = (res) => {
  const payload = res?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.esmps)) return payload.esmps;
  return [];
};

const countByStatus = (items) => {
  const counts = {
    total: items.length,
    submitted: 0,
    pending: 0,
    overdue: 0,
    approved: 0,
    returned: 0,
    rejected: 0,
  };

  for (const item of items) {
    switch (item?.status) {
      case "Submitted":
        counts.submitted += 1;
        break;
      case "Pending":
        counts.pending += 1;
        break;
      case "Overdue":
        counts.overdue += 1;
        break;
      case "Approved":
        counts.approved += 1;
        break;
      case "Returned":
        counts.returned += 1;
        break;
      case "Rejected":
        counts.rejected += 1;
        break;
      default:
        break;
    }
  }

  return counts;
};

const aggregateByKey = (items, getKey, { missingLabel }) => {
  const map = new Map();

  for (const item of items) {
    const key = normalizeLabel(getKey(item), missingLabel);
    const bucket = map.get(key) || [];
    bucket.push(item);
    map.set(key, bucket);
  }

  const rows = Array.from(map.entries()).map(([key, bucket]) => ({
    key,
    ...countByStatus(bucket),
  }));

  rows.sort((a, b) => b.total - a.total || a.key.localeCompare(b.key));
  return rows;
};

const StatCard = ({ label, value, accentClass }) => {
  return (
    <div className={`bg-white p-4 rounded-xl shadow border-l-4 ${accentClass}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <h3 className="text-2xl font-bold">{value}</h3>
    </div>
  );
};

const Table = ({ title, rows, onSelectKey }) => {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={18} className="text-green-700" />
        <h3 className="text-lg font-semibold text-green-700">{title}</h3>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No data available.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-3 py-2 text-left">{title}</th>
                <th className="border px-3 py-2 text-right">Total</th>
                <th className="border px-3 py-2 text-right">Approved</th>
                <th className="border px-3 py-2 text-right">Pending</th>
                <th className="border px-3 py-2 text-right">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className={`hover:bg-green-50 ${onSelectKey ? "cursor-pointer" : ""}`}
                  onClick={onSelectKey ? () => onSelectKey(row.key) : undefined}
                  role={onSelectKey ? "button" : undefined}
                  tabIndex={onSelectKey ? 0 : undefined}
                  onKeyDown={
                    onSelectKey
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") onSelectKey(row.key);
                        }
                      : undefined
                  }
                >
                  <td className="border px-3 py-2 font-medium">{row.key}</td>
                  <td className="border px-3 py-2 text-right">{row.total}</td>
                  <td className="border px-3 py-2 text-right">{row.approved}</td>
                  <td className="border px-3 py-2 text-right">{row.pending}</td>
                  <td className="border px-3 py-2 text-right">{row.overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function ViewerSectorStatistics() {
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [search, setSearch] = useState("");

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const esmpRes = await api.get("/esmp/list");
      const esmpList = parseEsmpListPayload(esmpRes);
      setEsmps(Array.isArray(esmpList) ? esmpList : []);
    } catch (e) {
      console.error("Failed to load viewer sector statistics:", e);
      setError(e?.response?.data?.message || "Failed to load sector statistics");
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const statusTotals = useMemo(() => countByStatus(esmps), [esmps]);

  const bySector = useMemo(() => {
    return aggregateByKey(esmps, (e) => e?.sector, { missingLabel: "Unspecified sector" });
  }, [esmps]);

  const filteredSectors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bySector;
    return bySector.filter((row) => (row.key || "").toLowerCase().includes(q));
  }, [bySector, search]);

  const sectorDetails = useMemo(() => {
    if (!selectedSector) return null;
    const filtered = esmps.filter(
      (e) => normalizeLabel(e?.sector, "Unspecified sector") === selectedSector
    );
    const sorted = [...filtered].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    return { key: selectedSector, items: sorted, counts: countByStatus(sorted) };
  }, [esmps, selectedSector]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <ViewerSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading sector statistics...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ViewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <div>
            <h2 className="text-2xl font-bold">Sector Statistics</h2>
            <p className="text-sm mt-1">Read-only ESMP overview grouped by sector</p>
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            <div className="w-full md:w-72">
              <label className="block text-xs font-semibold text-green-50 mb-1">Search sectors</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type a sector name..."
                className="w-full px-3 py-2 rounded-lg text-black border focus:ring-2 focus:ring-green-600 outline-none"
              />
            </div>

            <button
              onClick={loadStats}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <StatCard label="Total ESMPs" value={statusTotals.total} accentClass="border-green-600" />
          <StatCard label="Approved" value={statusTotals.approved} accentClass="border-green-500" />
          <StatCard label="Pending" value={statusTotals.pending} accentClass="border-yellow-500" />
          <StatCard label="Submitted" value={statusTotals.submitted} accentClass="border-blue-500" />
          <StatCard label="Overdue" value={statusTotals.overdue} accentClass="border-red-500" />
          <StatCard
            label="Returned / Rejected"
            value={statusTotals.returned + statusTotals.rejected}
            accentClass="border-gray-500"
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
          Tip: click a sector row to view details.
        </div>

        <Table title="Sector" rows={filteredSectors} onSelectKey={setSelectedSector} />
      </main>

      {sectorDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-green-700">Sector: {sectorDetails.key}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Total: {sectorDetails.counts.total} · Approved: {sectorDetails.counts.approved} ·
                    Pending: {sectorDetails.counts.pending} · Submitted: {sectorDetails.counts.submitted} ·
                    Overdue: {sectorDetails.counts.overdue}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedSector(null)}
                  className="text-3xl text-gray-500 hover:text-gray-700"
                  aria-label="Close sector details"
                >
                  ×
                </button>
              </div>

              {sectorDetails.items.length === 0 ? (
                <p className="text-sm text-gray-500">No ESMPs found for this sector.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-3 py-2 text-left">ESMP ID</th>
                        <th className="border px-3 py-2 text-left">Project</th>
                        <th className="border px-3 py-2 text-left">District</th>
                        <th className="border px-3 py-2 text-left">Status</th>
                        <th className="border px-3 py-2 text-left">Submitted</th>
                        <th className="border px-3 py-2 text-left">Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorDetails.items.map((item) => (
                        <tr key={item.id} className="hover:bg-green-50">
                          <td className="border px-3 py-2 font-medium text-blue-700">
                            {item.esmp_id || "N/A"}
                          </td>
                          <td className="border px-3 py-2">{item.project_name || "N/A"}</td>
                          <td className="border px-3 py-2">{item.district || "—"}</td>
                          <td className="border px-3 py-2">{item.status || "Unknown"}</td>
                          <td className="border px-3 py-2">
                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="border px-3 py-2">
                            {item.file_path ? (
                              <button
                                type="button"
                                onClick={() => downloadEsmpFile(item)}
                                className="text-blue-600 hover:underline"
                              >
                                {item.file_name || "Open document"}
                              </button>
                            ) : (
                              <span className="text-gray-500">No file</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedSector(null)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
